/**
 * Карточка элемента.
 *
 * Открывается боковой панелью поверх списка: контекст списка не теряется
 * (документ «Структура интерфейса», §10). Сначала показывается главное —
 * название, статус, содержимое; служебные метаданные и полный набор свойств
 * раскрываются ниже.
 *
 * Редактирование типоспецифичных полей происходит здесь же, формой,
 * собранной из реестра.
 */

import { ArchiveIcon, ArchiveRestoreIcon, Loader2Icon, PencilIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  getDetailProperties,
  getItemTypeDefinition,
  getJsonProperties,
  PRIORITIES,
  PRIORITY_LABELS_RU,
  type ItemDto,
  type Priority,
  type UserDto,
  type WorkstreamDto,
} from '@project-os/contracts';
import { ApiError } from '@/api/client';
import { useArchiveItem, useItem, useRestoreItem, useUpdateItem } from '@/api/items';
import { ItemStatusBadge } from '@/components/common/status-badge';
import { ErrorState } from '@/components/common/states';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { formatDate, formatDateTime } from '@/lib/format';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserAvatar } from '@/components/common/user-avatar';
import { CommentsSection } from './CommentsSection';
import { ItemTypeIcon } from './item-icon';
import { RelationSuggestions } from './RelationSuggestions';
import { RelationsSection } from './RelationsSection';
import { RevisionsSection } from './RevisionsSection';
import { AccountAccessSection } from '../resources/AccountAccessSection';
import { AttachmentsSection } from './AttachmentsSection';
import { ReviewSection } from './ReviewSection';
import { VersionsSection } from './VersionsSection';
import { PropertyField } from './PropertyField';
import { PropertyList } from './PropertyValue';

const NONE = '__none__';

export function ItemDetailPanel({
  projectId,
  itemId,
  onClose,
  onOpenItem,
  canEdit,
  canReview,
  workstreams,
  members,
}: {
  projectId: string;
  itemId: string | null;
  onClose: () => void;
  /** Переход к связанному элементу без закрытия панели. */
  onOpenItem: (itemId: string) => void;
  canEdit: boolean;
  /** Право выносить решение по материалу. У наблюдателя его нет. */
  canReview: boolean;
  workstreams: WorkstreamDto[];
  members: UserDto[];
}) {
  const { data: item, isPending, isError, error, refetch } = useItem(projectId, itemId);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setEditing(false);
  }, [itemId]);

  return (
    <Sheet open={Boolean(itemId)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-xl">
        <SheetTitle className="sr-only">Карточка элемента</SheetTitle>

        {isPending && itemId ? (
          <div className="space-y-3 p-6">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : isError ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : item ? (
          editing ? (
            <ItemEditForm
              projectId={projectId}
              item={item}
              workstreams={workstreams}
              members={members}
              onDone={() => setEditing(false)}
            />
          ) : (
            <ItemView
              projectId={projectId}
              item={item}
              members={members}
              canEdit={canEdit}
              canReview={canReview}
              onEdit={() => setEditing(true)}
              onOpenItem={onOpenItem}
            />
          )
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

// --- Просмотр ------------------------------------------------------------

function ItemView({
  projectId,
  item,
  members,
  canEdit,
  canReview,
  onEdit,
  onOpenItem,
}: {
  projectId: string;
  item: ItemDto;
  members: UserDto[];
  canEdit: boolean;
  canReview: boolean;
  onEdit: () => void;
  onOpenItem: (itemId: string) => void;
}) {
  const [tab, setTab] = useState('overview');
  const definition = getItemTypeDefinition(item.type);
  const archive = useArchiveItem(projectId);
  const restore = useRestoreItem(projectId);

  const toggleArchive = async () => {
    try {
      if (item.archivedAt) {
        await restore.mutateAsync(item.id);
        toast.success('Элемент восстановлен');
      } else {
        await archive.mutateAsync(item.id);
        toast.success('Элемент архивирован. Данные сохранены.');
      }
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось выполнить действие');
    }
  };

  const busy = archive.isPending || restore.isPending;

  return (
    <>
      <header className="flex shrink-0 items-start gap-3 border-b px-5 py-4">
        <ItemTypeIcon type={item.type} className="mt-0.5 size-5" />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground">{definition.labelRu}</p>
          <h2 className="mt-0.5 text-base leading-snug font-semibold">{item.title}</h2>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <ItemStatusBadge type={item.type} status={item.status} />
            {item.isNextAction ? (
              <span className="text-2xs font-medium text-primary">Следующее действие</span>
            ) : null}
            {item.archivedAt ? (
              <span className="text-2xs text-muted-foreground">
                В архиве с {formatDate(item.archivedAt)}
              </span>
            ) : null}
          </div>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab} className="min-h-0 flex-1 gap-0">
        <TabsList className="mx-5 mt-3 w-[calc(100%-2.5rem)]">
          <TabsTrigger value="overview">Обзор</TabsTrigger>
          {definition.supportsAttachments ? (
            <TabsTrigger value="files">
              Файлы
              {item.counters.attachments > 0 ? (
                <span className="ml-1 text-muted-foreground tabular-nums">
                  {item.counters.attachments}
                </span>
              ) : null}
            </TabsTrigger>
          ) : null}
          <TabsTrigger value="relations">
            Связи
            {item.counters.relations > 0 ? (
              <span className="ml-1 text-muted-foreground tabular-nums">
                {item.counters.relations}
              </span>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="comments">
            Обсуждение
            {item.counters.comments > 0 ? (
              <span className="ml-1 text-muted-foreground tabular-nums">
                {item.counters.comments}
              </span>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="history">История</TabsTrigger>
        </TabsList>

        <TabsContent value="files" className="flex min-h-0 flex-1">
          <ScrollArea className="h-full">
            <div className="space-y-5 px-5 py-4">
              <section>
                <h3 className="mb-2 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
                  Приложенные файлы
                </h3>
                <AttachmentsSection
                  projectId={projectId}
                  itemId={item.id}
                  attachments={item.attachments}
                  canEdit={canEdit}
                />
              </section>

              {definition.supportsVersions ? (
                <>
                  <Separator />
                  <section>
                    <h3 className="mb-2 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
                      Версии
                    </h3>
                    <VersionsSection
                      projectId={projectId}
                      itemId={item.id}
                      versions={item.versions}
                      attachments={item.attachments}
                      canEdit={canEdit}
                    />
                  </section>
                </>
              ) : null}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="relations" className="flex min-h-0 flex-1">
          <ScrollArea className="h-full">
            <div className="space-y-4 px-5 py-4">
              <RelationSuggestions
                projectId={projectId}
                itemId={item.id}
                canEdit={canEdit}
                onOpenItem={onOpenItem}
              />

              <RelationsSection
                projectId={projectId}
                itemId={item.id}
                canEdit={canEdit}
                onOpenItem={onOpenItem}
              />
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="comments" className="flex min-h-0 flex-1">
          <ScrollArea className="h-full">
            <div className="px-5 py-4">
              <CommentsSection projectId={projectId} itemId={item.id} canComment={canEdit} />
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="history" className="flex min-h-0 flex-1">
          <ScrollArea className="h-full">
            <div className="px-5 py-4">
              <RevisionsSection projectId={projectId} itemId={item.id} />
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="overview" className="flex min-h-0 flex-1">
          <ScrollArea className="h-full">
            <div className="space-y-5 px-5 py-4">
              {definition.warningRu ? (
                <Alert>
                  <AlertDescription className="text-xs leading-relaxed">
                    {definition.warningRu}
                  </AlertDescription>
                </Alert>
              ) : null}

              {item.type === 'ACCOUNT' ? (
                <section>
                  <h3 className="mb-2 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
                    Доступ есть у
                  </h3>
                  <AccountAccessSection
                    projectId={projectId}
                    itemId={item.id}
                    members={members}
                    canEdit={canEdit}
                  />
                </section>
              ) : null}

              {definition.supportsReview ? (
                <section>
                  <h3 className="mb-2 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
                    Проверка
                  </h3>
                  <ReviewSection
                    projectId={projectId}
                    itemId={item.id}
                    itemType={item.type}
                    status={item.status}
                    reviews={item.reviews}
                    canReview={canReview}
                  />
                </section>
              ) : null}

              {item.bodyMd ? (
                <section>
                  <h3 className="mb-1.5 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
                    Описание
                  </h3>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{item.bodyMd}</p>
                </section>
              ) : null}

              <section>
                <h3 className="mb-2 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
                  Поля типа
                </h3>
                <PropertyList
                  type={item.type}
                  properties={item.properties}
                  detail={item.detail}
                  members={members}
                  showEmpty
                />
              </section>

              <Separator />

              <section>
                <h3 className="mb-2 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
                  Служебное
                </h3>
                <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-[minmax(0,180px)_minmax(0,1fr)]">
                  <MetaRow label="Направление" value={item.workstream?.name ?? null} />
                  <MetaRow label="Ответственный" value={item.owner?.name ?? null} />
                  <MetaRow
                    label="Приоритет"
                    value={item.priority ? PRIORITY_LABELS_RU[item.priority] : null}
                  />
                  <MetaRow label="Срок" value={item.dueDate ? formatDate(item.dueDate) : null} />
                  <div className="contents">
                    <dt className="text-xs text-muted-foreground">Автор</dt>
                    <dd className="flex items-center gap-1.5 text-sm">
                      <UserAvatar user={item.createdBy} size="sm" />
                      {item.createdBy.name}
                    </dd>
                  </div>
                  <MetaRow label="Создан" value={formatDateTime(item.createdAt)} />
                  <MetaRow label="Изменён" value={formatDateTime(item.updatedAt)} />
                  {item.completedAt ? (
                    <MetaRow label="Завершён" value={formatDateTime(item.completedAt)} />
                  ) : null}
                </dl>
              </section>
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>

      {canEdit ? (
        <footer className="flex shrink-0 items-center justify-between gap-2 border-t px-5 py-3">
          <Button variant="outline" size="sm" disabled={busy} onClick={() => void toggleArchive()}>
            {busy ? (
              <Loader2Icon className="animate-spin" />
            ) : item.archivedAt ? (
              <ArchiveRestoreIcon />
            ) : (
              <ArchiveIcon />
            )}
            {item.archivedAt ? 'Восстановить' : 'В архив'}
          </Button>
          <Button size="sm" onClick={onEdit}>
            <PencilIcon />
            Изменить
          </Button>
        </footer>
      ) : null}
    </>
  );
}

function MetaRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="contents">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={value ? 'text-sm' : 'text-sm text-muted-foreground/70'}>{value ?? '—'}</dd>
    </div>
  );
}

// --- Редактирование ------------------------------------------------------

interface EditForm {
  title: string;
  bodyMd: string;
  status: string;
  priority: string;
  workstreamId: string;
  ownerId: string;
  dueDate: string;
  isNextAction: boolean;
  properties: Record<string, unknown>;
  detail: Record<string, unknown>;
}

function ItemEditForm({
  projectId,
  item,
  workstreams,
  members,
  onDone,
}: {
  projectId: string;
  item: ItemDto;
  workstreams: WorkstreamDto[];
  members: UserDto[];
  onDone: () => void;
}) {
  const definition = getItemTypeDefinition(item.type);
  const update = useUpdateItem(projectId);

  const form = useForm<EditForm>({
    defaultValues: {
      title: item.title,
      bodyMd: item.bodyMd ?? '',
      status: item.status,
      priority: item.priority ?? NONE,
      workstreamId: item.workstream?.id ?? NONE,
      ownerId: item.owner?.id ?? NONE,
      dueDate: item.dueDate ?? '',
      isNextAction: item.isNextAction,
      properties: { ...item.properties },
      detail: item.detail ? { ...item.detail } : { currency: 'EUR' },
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    const clean = (source: Record<string, unknown>) =>
      Object.fromEntries(
        Object.entries(source).map(([key, value]) => [key, value === '' ? null : value]),
      );

    try {
      await update.mutateAsync({
        itemId: item.id,
        title: values.title,
        bodyMd: values.bodyMd || null,
        status: values.status,
        priority: values.priority === NONE ? null : (values.priority as Priority),
        workstreamId: values.workstreamId === NONE ? null : values.workstreamId,
        ownerId: values.ownerId === NONE ? null : values.ownerId,
        dueDate: values.dueDate || null,
        isNextAction: values.isNextAction,
        properties: clean(values.properties),
        detail: definition.detailTable ? clean(values.detail) : null,
      });
      toast.success('Изменения сохранены');
      onDone();
    } catch (error) {
      if (error instanceof ApiError && error.fields) {
        for (const [field, message] of Object.entries(error.fields)) {
          form.setError(field as Parameters<typeof form.setError>[0], { message });
        }
        toast.error('Проверьте заполнение полей');
      } else {
        toast.error(error instanceof ApiError ? error.message : 'Не удалось сохранить');
      }
    }
  });

  return (
    <form onSubmit={onSubmit} className="flex h-full flex-col" noValidate>
      <header className="shrink-0 border-b px-5 py-4">
        <p className="text-xs text-muted-foreground">{definition.labelRu}</p>
        <h2 className="mt-0.5 text-base font-semibold">Изменение элемента</h2>
      </header>

      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-4 px-5 py-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit-title">Название</Label>
            <Input id="edit-title" {...form.register('title', { required: true })} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-body">Описание</Label>
            <Textarea id="edit-body" rows={4} {...form.register('bodyMd')} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="edit-status">Статус</Label>
              <Select
                value={form.watch('status')}
                onValueChange={(value) => form.setValue('status', value)}
              >
                <SelectTrigger id="edit-status" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {definition.statuses.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.labelRu}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {definition.supportsPriority ? (
              <div className="space-y-1.5">
                <Label htmlFor="edit-priority">Приоритет</Label>
                <Select
                  value={form.watch('priority')}
                  onValueChange={(value) => form.setValue('priority', value)}
                >
                  <SelectTrigger id="edit-priority" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Не задан</SelectItem>
                    {PRIORITIES.map((priority) => (
                      <SelectItem key={priority} value={priority}>
                        {PRIORITY_LABELS_RU[priority]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="edit-workstream">Направление</Label>
              <Select
                value={form.watch('workstreamId')}
                onValueChange={(value) => form.setValue('workstreamId', value)}
              >
                <SelectTrigger id="edit-workstream" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Без направления</SelectItem>
                  {workstreams.map((workstream) => (
                    <SelectItem key={workstream.id} value={workstream.id}>
                      {workstream.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="edit-owner">Ответственный</Label>
              <Select
                value={form.watch('ownerId')}
                onValueChange={(value) => form.setValue('ownerId', value)}
              >
                <SelectTrigger id="edit-owner" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Не назначен</SelectItem>
                  {members.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {definition.supportsDueDate ? (
              <div className="space-y-1.5">
                <Label htmlFor="edit-due">Срок</Label>
                <Input id="edit-due" type="date" {...form.register('dueDate')} />
              </div>
            ) : null}
          </div>

          {definition.supportsNextAction ? (
            <div className="flex items-center gap-2">
              <Checkbox
                id="edit-next"
                checked={form.watch('isNextAction')}
                onCheckedChange={(checked) => form.setValue('isNextAction', checked === true)}
              />
              <Label htmlFor="edit-next" className="font-normal">
                Следующее действие
              </Label>
            </div>
          ) : null}

          <Separator />

          <div className="space-y-4">
            <h3 className="text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
              Поля типа «{definition.labelRu.toLowerCase()}»
            </h3>
            {getJsonProperties(item.type).map((property) => (
              <PropertyField
                key={property.key}
                control={form.control}
                name={`properties.${property.key}` as never}
                property={property}
                members={members}
              />
            ))}
            {getDetailProperties(item.type).map((property) => (
              <PropertyField
                key={property.key}
                control={form.control}
                name={`detail.${property.key}` as never}
                currencyName={'detail.currency' as never}
                property={property}
                members={members}
              />
            ))}
          </div>
        </div>
      </ScrollArea>

      <footer className="flex shrink-0 items-center justify-end gap-2 border-t px-5 py-3">
        <Button type="button" variant="outline" size="sm" onClick={onDone}>
          Отмена
        </Button>
        <Button type="submit" size="sm" disabled={update.isPending}>
          {update.isPending ? <Loader2Icon className="animate-spin" /> : null}
          Сохранить
        </Button>
      </footer>
    </form>
  );
}
